import { NgClass } from '@angular/common';
import { Component, computed, inject, input, model, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { VirtualFenceService } from '@vectronic/base-map';
import { dsDtos } from '@vectronic/openApi-admin';
import { dsEnums, userDtos, webDtos } from '@vectronic/openApi-customer';
import { AlertComponent } from '@vectronic/ui';
import { ScheduleHelp, ScheduleResponseModel } from '@vectronic/util';
import { of } from 'rxjs';
import { GpsXFileImportService } from '../../services/gps-x-file-import.service';
import { RemoteCommandProviderService } from '../../services/remote-command-provider.service';
import { ScheduleServices } from '../../services/schedule.service';
import { CameraScheduleComponent } from '../camera/camera-schedule/camera-schedule.component';
import { GenericScheduleComponent } from '../generic-schedule/generic-schedule.component';
import { VirtualFenceViewComponent } from '../virtual-fence-view/virtual-fence-view.component';

@Component({
  selector: 'rc-schedule-view',
  templateUrl: './schedule-view.component.html',
  styleUrls: ['./schedule-view.component.scss'],
  imports: [
    VirtualFenceViewComponent,
    CameraScheduleComponent,
    MatButton,
    MatIcon,
    NgClass,
    MatTooltip,
    GenericScheduleComponent,
    AlertComponent,
  ],
})
export class ScheduleViewComponent {
  scheduleServices = inject(ScheduleServices);
  virtualFenceService = inject(VirtualFenceService);
  remoteCommandProviderService = inject(RemoteCommandProviderService, { optional: true });
  gpsXFileImportService = inject(GpsXFileImportService);

  isAdmin = input<boolean>();
  readonly = input<boolean>();
  isConfirmation = input<boolean>();
  selectedCollarIds = input<number[] | undefined>();
  videoFormatsOptions = input<{ value: number; label: string }[]>();
  photoFormatsOptions = input<{ value: number; label: string }[]>();
  showClear = input<boolean>();
  showSubmit = input<boolean>();
  showReset = input<boolean>();
  showRemoveSchedule = input(true);
  shouldUpdateSchedules = input(false);
  hideChangedSchedules = input<boolean>();
  virtualFenceCleared = input<boolean>();
  isRulesDeletable = input<boolean>();
  onlyShow = input<(dsEnums.ScheduleType | 'VIRTUAL_FENCE_MAP')[]>();
  forceShow = input<(dsEnums.ScheduleType | 'VIRTUAL_FENCE_MAP')[]>();
  defaultVirtualFenceConstructionRules = input<webDtos.VirtualFenceConstructionRule[]>();
  showNoCameraWarning = input<boolean>();
  showCameraModeWarning = input<boolean>();
  showNoCameraPermissionWarning = input<boolean>();
  showVfModeOffWarning = input<boolean>();
  showVfModeWarning = input<boolean>();
  showProximityWarning = input<boolean>();
  hasHighActivityCameraTrigger = input<boolean>();
  showScheduleRemove = input<boolean>(true);
  permanentStartDate = input<string>('2000-01-01');
  permanentEndDate = input<string>('2050-01-01');
  permanentEndDateByCollar = input<Record<number, string> | undefined>();
  isPositionsLoading = input<boolean>(false);
  mapPositions = input<webDtos.MapPositionsList[]>([]);
  userPreferences = input<userDtos.UserPreferences>();
  showOverrideWarning = input<boolean>();
  initialScheduleTypes = input<dsEnums.ScheduleType[]>();
  initialHasVirtualFence = input<boolean>();
  remoteSchedulesOverride = input<ScheduleResponseModel>();
  hideCameraFormat = input<boolean>();
  hasMultipleCameraTypes = input<boolean>();
  showLoadFromTemplates = input<boolean>(false);
  showSaveAsTemplate = input<boolean>(true);
  showImportJson = input<boolean>(true);
  ignoreShortHeaderMessage = input<boolean>(false);
  virtualFenceNotAvailableForIds = input<number[]>();
  scheduleTitles = input<Record<dsEnums.ScheduleType, string>>();
  warningByScheduleType = input<Map<dsEnums.ScheduleType | 'VIRTUAL_FENCE_MAP', string>>();
  errorsByScheduleType = input<Map<dsEnums.ScheduleType | 'VIRTUAL_FENCE_MAP', string>>();
  hideRulesByScheduleType = input<Map<dsEnums.ScheduleType | 'VIRTUAL_FENCE_MAP', boolean>>();
  hideConstructionRules = input<boolean>(false);
  showPreview = model<boolean>();

  submitSchedule = output<dsDtos.Schedule[]>();
  clear = output();
  resetToDefaultPolygons = output();
  clickSendCameraRequestPermission = output();
  loadPositions = output<{
    deviceIds: number[];
    lastXRecords?: number;
    utcFrom?: string;
    utcTo?: string;
    isAnimation?: boolean;
  }>();

  remoteSchedulesFromService = toSignal(
    this.remoteCommandProviderService?.remoteSchedules$ ?? of(),
  );
  remoteSchedules = computed(
    () => this.remoteSchedulesOverride() ?? this.remoteSchedulesFromService(),
  );

  isFilled = computed(() => {
    const isFilledExceptCamera = ScheduleHelp.services()?.some((type) =>
      this.scheduleServices[type]?.isFilledAndChanged(),
    );
    const isFilledCamera = this.scheduleServices.CAMERA.isFilledAndChanged();
    return (
      isFilledExceptCamera || isFilledCamera || this.virtualFenceService.isVirtualFenceChanged()
    );
  });

  hasUserProximity = computed(() =>
    this.scheduleServices
      .defaultSchedules()
      ?.some((schedule) => schedule.userDefined && schedule.scheduleType === 'PROXIMITY'),
  );

  hasUserVirtualFence = computed(() =>
    this.scheduleServices
      .defaultSchedules()
      ?.some((schedule) => schedule.userDefined && schedule.scheduleType === 'VIRTUAL_FENCE'),
  );

  hasUserCommunication = computed(() =>
    this.scheduleServices
      .defaultSchedules()
      ?.some((schedule) => schedule.userDefined && schedule.scheduleType === 'COMMUNICATION'),
  );

  hasUserCamera = computed(() =>
    this.scheduleServices
      .defaultSchedules()
      ?.some((schedule) => schedule.userDefined && schedule.scheduleType === 'CAMERA'),
  );

  private hasSchedule(type: dsEnums.ScheduleType) {
    if (this.forceShow()?.includes(type)) {
      return true;
    }
    if (this.onlyShow()?.length && !this.onlyShow()?.includes(type)) {
      return false;
    }
    return (
      this.scheduleServices.defaultSchedules()?.some(({ scheduleType }) => scheduleType === type) ||
      (!this.readonly() &&
        this.remoteSchedules()?.[type]?.some((deviceId: number) =>
          this.selectedCollarIds()?.includes(deviceId),
        ))
    );
  }

  hasGps = computed(() => this.hasSchedule('GPS'));
  hasSolexGps = computed(() => this.hasSchedule('SOLEX_GPS'));
  hasProximity = computed(() => this.hasSchedule('PROXIMITY'));
  hasBeacon = computed(() => this.hasSchedule('VHF'));
  hasCommunication = computed(() => this.hasSchedule('COMMUNICATION'));
  hasCamera = computed(() => this.hasSchedule('CAMERA'));
  hasVirtualFenceSchedule = computed(() => this.hasSchedule('VIRTUAL_FENCE'));

  shouldShowWarning(type: dsEnums.ScheduleType) {
    if (!this.showOverrideWarning()) return false;
    const initial = this.initialScheduleTypes();
    if (initial === undefined) return true;
    return initial.includes(type);
  }

  shouldShowVirtualFenceWarning() {
    if (!this.showOverrideWarning()) return false;
    const initial = this.initialHasVirtualFence();
    if (initial === undefined) return true;
    return initial;
  }

  hasVirtualFence = computed(() => {
    if (this.forceShow()?.includes('VIRTUAL_FENCE_MAP')) {
      return true;
    }
    if (this.onlyShow()?.length && !this.onlyShow()?.includes('VIRTUAL_FENCE_MAP')) {
      return false;
    }
    if (this.onlyShow()?.includes('VIRTUAL_FENCE_MAP')) {
      return true;
    }
    return (
      (this.isConfirmation() && this.virtualFenceService.isVirtualFenceFilled()) ||
      (!this.isConfirmation() &&
        (this.virtualFenceService.constructionRules()?.length ||
          this.defaultVirtualFenceConstructionRules()?.length)) ||
      this.virtualFenceCleared() ||
      this.virtualFenceService.isEmptyVirtualFence() ||
      (!this.readonly() &&
        this.remoteSchedules()?.VIRTUAL_FENCE?.some((deviceId) =>
          this.selectedCollarIds()?.includes(deviceId),
        ))
    );
  });

  effectivePermanentEndDate = computed(() => {
    const map = this.permanentEndDateByCollar();
    const selected = this.selectedCollarIds();
    if (map && selected?.length === 1) {
      const date = map[selected[0]];
      if (date) return date;
    }
    return this.permanentEndDate();
  });

  shouldShowGpsXFileButton(type: dsEnums.ScheduleType): boolean {
    return this.gpsXFileImportService.canLoad(type, this.readonly());
  }

  gpsXFileAcceptFor(type: dsEnums.ScheduleType): string | undefined {
    return this.gpsXFileImportService.getAcceptedFileExtension(type);
  }

  warningMessagesFor(type: dsEnums.ScheduleType | 'VIRTUAL_FENCE_MAP'): string[] {
    const msg = this.warningByScheduleType()?.get(type);
    return msg ? [msg] : [];
  }

  errorMessagesFor(type: dsEnums.ScheduleType | 'VIRTUAL_FENCE_MAP'): string[] {
    const msg = this.errorsByScheduleType()?.get(type);
    return msg ? [msg] : [];
  }

  hideRulesFor(type: dsEnums.ScheduleType | 'VIRTUAL_FENCE_MAP'): boolean {
    return this.hideRulesByScheduleType()?.get(type) ?? false;
  }

  isDisabledByEmptySchedule(scheduleType: dsEnums.ScheduleType): boolean {
    return ScheduleHelp.services()
      .filter((t) => t !== scheduleType)
      .some((t) => this.scheduleServices[t]?.isEmptyScheduleSignal());
  }

  onSubmitChanges() {
    const schedules: dsDtos.Schedule[] = ScheduleHelp.services()
      ?.filter((type) => this.scheduleServices[type]?.isFilledAndChanged())
      ?.map((scheduleType) => ({
        scheduleType,
        userDefined: true,
        rules: Object.values(this.scheduleServices[scheduleType].rules$.value).filter(Boolean),
      }));
    if (this.scheduleServices.CAMERA.isFilledAndChanged()) {
      schedules.push({
        scheduleType: 'CAMERA',
        userDefined: true,
        cameraScheduleRules: Object.values(this.scheduleServices.CAMERA.rules$.value).filter(
          Boolean,
        ),
      });
    }
    this.submitSchedule.emit(schedules);
  }

  onClear() {
    this.clear.emit();
    this.resetToDefaultPolygons.emit();
  }
}
